/*
 * Creative Analysis — Overview tab (2026-10-28, plan 2026-10-28-001 PR 2).
 *
 * Key metrics for one product, window and segment: the value, the change
 * against the prior window of the same length, and a daily trend line with
 * the prior period dashed behind it. All the numbers come from
 * overview-model.ts; this file only draws them.
 */
import { Group, Paper, SegmentedControl, SimpleGrid, Stack, Text, UnstyledButton } from "@mantine/core";

import {
  change,
  changeTone,
  familyRanking,
  overviewModel,
  SEGMENT_LABEL,
  type FamilyRow,
  type Segment,
  type Tile,
  type TileFormat,
  type TileSpark,
  type WindowDays,
} from "./overview-model";
import type { CoverageProduct } from "./types";

const TONE_COLOR = {
  good: "var(--color-success)",
  bad: "var(--color-danger)",
  neutral: "var(--color-text-muted)",
} as const;

function fmt(v: number | null, format: TileFormat, cur: string): string {
  if (v === null || !Number.isFinite(v)) return "—";
  switch (format) {
    case "money":
      return `${cur}${Math.round(v).toLocaleString()}`;
    case "money2":
      return `${cur}${v.toFixed(2)}`;
    case "ratio":
      return v.toFixed(2);
    default:
      return Math.round(v).toLocaleString();
  }
}

/** Small inline trend line: current window solid, prior window dashed, the
 *  provisional FunnelFlux tail dashed in the accent colour. */
function Sparkline({ spark }: { spark: TileSpark }) {
  const W = 120;
  const H = 32;
  const vals = [...spark.cur, ...spark.prior].filter((v): v is number => v !== null && Number.isFinite(v));
  if (vals.length < 2) return <div style={{ height: H }} />;
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const span = hi - lo || 1;
  const n = spark.cur.length;
  const x = (i: number) => (n === 1 ? W / 2 : (i / (n - 1)) * W);
  const y = (v: number) => H - 2 - ((v - lo) / span) * (H - 4);
  const path = (pts: Array<number | null>, from = 0, to = pts.length - 1): string => {
    let d = "";
    let pen = false;
    for (let i = from; i <= to; i++) {
      const v = pts[i];
      if (v === null || v === undefined || !Number.isFinite(v)) {
        pen = false;
        continue;
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    }
    return d;
  };
  const solidEnd = Math.max(0, n - 1 - spark.provisional);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-hidden="true">
      <path d={path(spark.prior)} fill="none" stroke="var(--color-text-faint)" strokeWidth={1.2} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      <path d={path(spark.cur, 0, solidEnd)} fill="none" stroke="var(--color-accent)" strokeWidth={1.8} vectorEffect="non-scaling-stroke" />
      {spark.provisional > 0 && (
        <path d={path(spark.cur, solidEnd, n - 1)} fill="none" stroke="var(--color-accent)" strokeOpacity={0.45} strokeWidth={1.8} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
      )}
    </svg>
  );
}

function TileCard({ tile, cur, w }: { tile: Tile; cur: string; w: WindowDays }) {
  const c = change(tile);
  const tone = changeTone(tile, w);
  return (
    <Paper withBorder p="sm" radius="md">
      <Text fz="xs" c="dimmed" fw={500}>
        {tile.label}
      </Text>
      <Group gap={8} align="baseline" wrap="nowrap">
        <Text fz="lg" fw={700}>
          {fmt(tile.value, tile.format, cur)}
        </Text>
        {c !== null && (
          <Text fz="xs" fw={600} style={{ color: TONE_COLOR[tone] }}>
            {c > 0 ? "+" : ""}
            {(c * 100).toFixed(1)}%
          </Text>
        )}
      </Group>
      <Text fz="xs" c="dimmed">
        was {fmt(tile.prior, tile.format, cur)}
      </Text>
      {tile.spark ? <Sparkline spark={tile.spark} /> : <div style={{ height: 32 }} />}
    </Paper>
  );
}

function FamilyList({ title, rows, cur, onOpen }: { title: string; rows: FamilyRow[]; cur: string; onOpen?: () => void }) {
  return (
    <Paper withBorder p="sm" radius="md">
      <Text fz="sm" fw={600} mb={6}>
        {title}
      </Text>
      {rows.length === 0 ? (
        <Text fz="xs" c="dimmed">
          Not enough families with real spend in this window.
        </Text>
      ) : (
        <Stack gap={4}>
          {rows.map((r) => (
            <UnstyledButton key={r.key} onClick={onOpen} title="Open Explore, ranked by concept">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text fz="sm" truncate style={{ minWidth: 0 }}>
                  {r.label}
                </Text>
                <Text fz="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                  {cur}
                  {Math.round(r.sp).toLocaleString()} · <b>{r.roas.toFixed(2)}</b>
                </Text>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export interface OverviewProps {
  product: CoverageProduct;
  window: WindowDays;
  segment: Segment;
  onSegment: (s: Segment) => void;
  /** Jump to Explore ranked by concept family. */
  onOpenFamilies?: () => void;
}

export function Overview({ product, window: w, segment, onSegment, onOpenFamilies }: OverviewProps) {
  const model = overviewModel(product, w, segment);
  const fam = familyRanking(product, w, segment);
  const ff = model.money === "ff";
  const notes: string[] = [];
  if (segment !== "all" && !model.segmentDaily && product.daily) {
    notes.push("Daily lines per segment need the retargeting split, which this snapshot couldn't read. Switch to All for trend lines.");
  }
  if (ff && segment !== "all") {
    notes.push("FunnelFlux has no per-segment daily split, so FF tiles show the window value and change only. Switch to All for their trend lines.");
  }
  if (ff && w <= 7) {
    notes.push(
      w === 3
        ? "3-day FunnelFlux figures are an early read: the last 2 days can still be catching up, so their changes are shown in grey."
        : "FunnelFlux's last 2 days can still be catching up (drawn faded on the FF lines).",
    );
  }
  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <SegmentedControl
          value={segment}
          onChange={(v) => onSegment(v as Segment)}
          data={(["pros", "ret", "all"] as const).map((s) => ({ value: s, label: SEGMENT_LABEL[s] }))}
          aria-label="Segment"
        />
        <Text fz="xs" c="dimmed">
          {model.window.start} → {model.window.end}
          {model.window.prior_start ? ` vs ${model.window.prior_start} → ${model.window.prior_end}` : ""} · revenue:{" "}
          {ff ? "FunnelFlux" : "Meta-reported"}
        </Text>
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
        {model.tiles.map((t) => (
          <TileCard key={t.id} tile={t} cur={product.cur} w={w} />
        ))}
      </SimpleGrid>
      {notes.map((n) => (
        <Text key={n} fz="xs" c="dimmed">
          {n}
        </Text>
      ))}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <FamilyList title={`Best concept families · ${SEGMENT_LABEL[segment]}`} rows={fam.top} cur={product.cur} onOpen={onOpenFamilies} />
        <FamilyList title={`Worst concept families · ${SEGMENT_LABEL[segment]}`} rows={fam.bottom} cur={product.cur} onOpen={onOpenFamilies} />
      </SimpleGrid>
    </Stack>
  );
}

export interface OverviewCardsProps {
  products: Array<{ id: string; label: string; product: CoverageProduct }>;
  window: WindowDays;
  segment: Segment;
  onSegment: (s: Segment) => void;
  onOpen: (id: string) => void;
}

/** All-products view: one compact card per product, in its own currency,
 *  never summed (accounts are never pooled). */
export function OverviewCards({ products, window: w, segment, onSegment, onOpen }: OverviewCardsProps) {
  return (
    <Stack gap="sm">
      <SegmentedControl
        value={segment}
        onChange={(v) => onSegment(v as Segment)}
        data={(["pros", "ret", "all"] as const).map((s) => ({ value: s, label: SEGMENT_LABEL[s] }))}
        aria-label="Segment"
        style={{ alignSelf: "flex-start" }}
      />
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
        {products.map(({ id, label, product }) => {
          const m = overviewModel(product, w, segment);
          const pick = (tid: string) => m.tiles.find((t) => t.id === tid)!;
          return (
            <UnstyledButton key={id} onClick={() => onOpen(id)} aria-label={`Open ${label}`}>
              <Paper withBorder p="sm" radius="md">
                <Group justify="space-between" mb={4}>
                  <Text fw={600}>{label}</Text>
                  <Text fz="xs" c="dimmed">
                    revenue: {m.money === "ff" ? "FunnelFlux" : "Meta"}
                  </Text>
                </Group>
                {(["spend", "revenue", "roas"] as const).map((tid) => {
                  const t = pick(tid);
                  const c = change(t);
                  return (
                    <Group key={tid} justify="space-between" gap="xs">
                      <Text fz="sm" c="dimmed">
                        {t.label}
                      </Text>
                      <Text fz="sm">
                        <b>{fmt(t.value, t.format, product.cur)}</b>
                        {c !== null && (
                          <span style={{ color: TONE_COLOR[changeTone(t, w)], marginLeft: 6, fontSize: "var(--text-xs)" }}>
                            {c > 0 ? "+" : ""}
                            {(c * 100).toFixed(1)}%
                          </span>
                        )}
                      </Text>
                    </Group>
                  );
                })}
              </Paper>
            </UnstyledButton>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
